import * as React from "react";
import * as RDialog from "@radix-ui/react-dialog";
import * as RTabs from "@radix-ui/react-tabs";
import * as RTooltip from "@radix-ui/react-tooltip";
import * as RMenu from "@radix-ui/react-dropdown-menu";
import { AlertTriangle, Check, Copy, Info, Inbox, Lock, X } from "lucide-react";
import { cn } from "./cn";
import { Button, Skeleton } from "./primitives";

export function PageHeader({ title, description, actions, meta }: { title: string; description?: React.ReactNode; actions?: React.ReactNode; meta?: React.ReactNode }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h1 className="font-display text-[1.375rem] font-semibold leading-7 tracking-[-0.015em] text-fg">{title}</h1>
        {description && <p className="mt-1 max-w-3xl text-sm text-muted">{description}</p>}
        {meta && <div className="mt-2 flex flex-wrap items-center gap-2">{meta}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function EmptyState({ title, description, action, icon }: { title: string; description?: React.ReactNode; action?: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-[var(--cm-radius)] border border-dashed border-border-strong bg-surface px-6 py-10 text-center">
      <div className="grid h-12 w-12 place-items-center rounded-full bg-brand-50 text-brand" aria-hidden>
        {icon ?? <Inbox className="h-6 w-6" />}
      </div>
      <p className="text-sm font-semibold text-fg">{title}</p>
      {description && <p className="max-w-md text-sm text-muted">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export interface ErrorLike {
  status?: number;
  code?: string;
  message?: string;
  requestId?: string;
}

/** Estado de erro: distingue permissão insuficiente / assinatura limitada de falha técnica. */
export function ErrorState({ error, onRetry }: { error: ErrorLike | Error | unknown; onRetry?: () => void }) {
  const e = (error ?? {}) as ErrorLike;
  const forbidden = e.status === 403;
  const limited = e.status === 402;
  return (
    <div role="alert" className="flex flex-col items-center justify-center gap-2 rounded-[var(--cm-radius)] border border-border bg-surface px-6 py-10 text-center">
      <div className={forbidden || limited ? "text-warning" : "text-danger"} aria-hidden>
        {forbidden ? <Lock className="h-8 w-8" /> : <AlertTriangle className="h-8 w-8" />}
      </div>
      <p className="text-sm font-semibold text-fg">
        {forbidden ? "Permissão insuficiente" : limited ? "Limite da assinatura" : e.status === 404 ? "Não encontrado" : "Não foi possível carregar"}
      </p>
      <p className="max-w-md text-sm text-muted">{e.message ?? "Erro inesperado."}</p>
      {e.requestId && <p className="text-xs text-subtle">Código para suporte: {e.requestId}</p>}
      {onRetry && !forbidden && (
        <Button variant="outline" size="sm" onClick={onRetry} className="mt-2">
          Tentar novamente
        </Button>
      )}
    </div>
  );
}

export function LoadingRows({ rows = 5, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn("flex flex-col gap-2", className)} aria-busy="true" aria-label="Carregando">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  );
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = "md",
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  size?: "sm" | "md" | "lg";
}) {
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-40 bg-black/30" />
        <RDialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[var(--cm-radius)] border border-border bg-surface shadow-lg focus:outline-none",
            size === "sm" ? "max-w-md" : size === "lg" ? "max-w-3xl" : "max-w-xl",
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
            <div>
              <RDialog.Title className="text-base font-semibold text-fg">{title}</RDialog.Title>
              {description ? <RDialog.Description className="mt-1 text-sm text-muted">{description}</RDialog.Description> : <RDialog.Description className="sr-only">{title}</RDialog.Description>}
            </div>
            <RDialog.Close className="rounded p-1 text-muted hover:bg-surface-muted focus-visible:outline-2 focus-visible:outline-focus" aria-label="Fechar">
              <X className="h-4 w-4" />
            </RDialog.Close>
          </div>
          <div className="px-5 py-4">{children}</div>
          {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-border px-5 py-3">{footer}</div>}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

/** Drawer lateral para detalhes que preservam o contexto da lista. */
export function Drawer({ open, onOpenChange, title, description, children, footer }: { open: boolean; onOpenChange: (o: boolean) => void; title: string; description?: React.ReactNode; children?: React.ReactNode; footer?: React.ReactNode }) {
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-40 bg-black/20" />
        <RDialog.Content className="fixed right-0 top-0 z-50 flex h-full w-full max-w-xl flex-col border-l border-border bg-surface shadow-lg focus:outline-none">
          <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
            <div className="min-w-0">
              <RDialog.Title className="truncate text-base font-semibold text-fg">{title}</RDialog.Title>
              {description ? <RDialog.Description className="mt-1 text-sm text-muted">{description}</RDialog.Description> : <RDialog.Description className="sr-only">{title}</RDialog.Description>}
            </div>
            <RDialog.Close className="rounded p-1 text-muted hover:bg-surface-muted focus-visible:outline-2 focus-visible:outline-focus" aria-label="Fechar">
              <X className="h-4 w-4" />
            </RDialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <div className="flex justify-end gap-2 border-t border-border px-5 py-3">{footer}</div>}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

export function Tabs({ tabs, value, onValueChange, defaultValue }: { tabs: Array<{ value: string; label: React.ReactNode; content: React.ReactNode }>; value?: string; onValueChange?: (v: string) => void; defaultValue?: string }) {
  return (
    <RTabs.Root value={value} onValueChange={onValueChange} defaultValue={defaultValue ?? tabs[0]?.value}>
      <RTabs.List className="mb-4 flex gap-1 overflow-x-auto border-b border-border" aria-label="Seções">
        {tabs.map((t) => (
          <RTabs.Trigger
            key={t.value}
            value={t.value}
            className="-mb-px whitespace-nowrap border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-focus data-[state=active]:border-brand data-[state=active]:text-brand-700"
          >
            {t.label}
          </RTabs.Trigger>
        ))}
      </RTabs.List>
      {tabs.map((t) => (
        <RTabs.Content key={t.value} value={t.value} className="focus:outline-none">
          {t.content}
        </RTabs.Content>
      ))}
    </RTabs.Root>
  );
}

export function TooltipProvider({ children }: { children: React.ReactNode }) {
  return <RTooltip.Provider delayDuration={200}>{children}</RTooltip.Provider>;
}

/** Tooltip acessível (foco e hover). Use para fórmulas/denominadores de métricas. */
export function Tooltip({ content, children, side }: { content: React.ReactNode; children: React.ReactElement; side?: "top" | "right" | "bottom" | "left" }) {
  return (
    <RTooltip.Root>
      <RTooltip.Trigger asChild>{children}</RTooltip.Trigger>
      <RTooltip.Portal>
        <RTooltip.Content side={side} sideOffset={6} className="z-50 max-w-xs rounded-md bg-fg px-3 py-2 text-xs text-white shadow-md">
          {content}
          <RTooltip.Arrow className="fill-[var(--cm-text)]" />
        </RTooltip.Content>
      </RTooltip.Portal>
    </RTooltip.Root>
  );
}

export function InfoTip({ content, label = "Mais informações" }: { content: React.ReactNode; label?: string }) {
  return (
    <Tooltip content={content}>
      <button type="button" className="inline-flex rounded text-subtle hover:text-fg focus-visible:outline-2 focus-visible:outline-focus" aria-label={label}>
        <Info className="h-3.5 w-3.5" />
      </button>
    </Tooltip>
  );
}

export const Menu = RMenu.Root;
export const MenuTrigger = RMenu.Trigger;
export function MenuContent({ children, align = "end" }: { children: React.ReactNode; align?: "start" | "end" | "center" }) {
  return (
    <RMenu.Portal>
      <RMenu.Content align={align} sideOffset={4} className="z-50 min-w-48 rounded-[var(--cm-radius)] border border-border bg-surface p-1 shadow-md">
        {children}
      </RMenu.Content>
    </RMenu.Portal>
  );
}
export function MenuItem({ children, onSelect, danger, disabled, className }: { children: React.ReactNode; onSelect?: () => void; danger?: boolean; disabled?: boolean; className?: string }) {
  return (
    <RMenu.Item
      disabled={disabled}
      onSelect={onSelect}
      className={cn("flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 text-sm outline-none data-[disabled]:opacity-50 data-[highlighted]:bg-surface-muted", danger ? "text-danger" : "text-fg", className)}
    >
      {children}
    </RMenu.Item>
  );
}
export const MenuSeparator = () => <RMenu.Separator className="my-1 h-px bg-border" />;
export const MenuLabel = ({ children }: { children: React.ReactNode }) => <RMenu.Label className="px-2 py-1 text-xs font-medium text-subtle">{children}</RMenu.Label>;

// ---- Tabela ----
export function TableWrap({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("w-full overflow-x-auto rounded-[var(--cm-radius)] border border-border bg-surface", className)}>{children}</div>;
}
export function Table({ className, ...p }: React.TableHTMLAttributes<HTMLTableElement>) {
  return <table className={cn("w-full border-collapse text-sm", className)} {...p} />;
}
export function Th({ className, numeric, ...p }: React.ThHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return <th scope="col" className={cn("sticky top-0 border-b border-border bg-brand-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted whitespace-nowrap", numeric && "text-right", className)} {...p} />;
}
export function Td({ className, numeric, ...p }: React.TdHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return <td className={cn("border-b border-border px-3 py-2 align-middle text-fg", numeric && "text-right tabular-nums whitespace-nowrap", className)} {...p} />;
}
export function Tr({ className, ...p }: React.HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn("hover:bg-brand-50/50", className)} {...p} />;
}

// ---- KPI ----
export function KpiCard({
  label,
  value,
  sublabel,
  formula,
  loading,
  emphasis = "primary",
  onClick,
  footnote,
}: {
  label: string;
  value: React.ReactNode;
  sublabel?: React.ReactNode;
  formula?: React.ReactNode;
  loading?: boolean;
  emphasis?: "primary" | "secondary" | "brand";
  onClick?: () => void;
  footnote?: React.ReactNode;
}) {
  // O card não é um <button> quando há tooltip (evita elementos interativos aninhados); a ação vira um link explícito.
  const brand = emphasis === "brand"; // card de destaque: Noite com a linha da venda acesa embaixo.
  return (
    <div
      className={cn(
        "relative flex min-w-0 flex-col gap-1 overflow-hidden rounded-[var(--cm-radius)] border p-4 text-left shadow-sm",
        brand ? "border-transparent bg-surface-brand pb-[calc(1rem+6px)] after:absolute after:inset-x-0 after:bottom-0 after:h-1.5 after:bg-highlight-on-brand" : "border-border bg-surface",
        onClick && !brand && "hover:border-brand-200",
      )}
    >
      <span className={cn("flex items-center gap-1.5 text-xs font-semibold", brand ? "text-on-brand-2" : "text-muted")}>
        {label}
        {formula && <InfoTip content={formula} label={`Fórmula de ${label}`} />}
      </span>
      {loading ? (
        <Skeleton className="h-7 w-24" />
      ) : (
        <span className={cn("truncate font-display font-semibold tabular-nums tracking-[-0.02em]", brand ? "text-on-brand" : "text-fg", emphasis === "secondary" ? "text-xl" : "text-[1.6rem] leading-8")}>{value}</span>
      )}
      {sublabel && <span className={cn("truncate text-xs", brand ? "text-on-brand-2" : "text-muted")}>{sublabel}</span>}
      {footnote && <span className={cn("text-[11px]", brand ? "text-on-brand-2" : "text-subtle")}>{footnote}</span>}
      {onClick && (
        <button type="button" onClick={onClick} className={cn("mt-1 self-start text-xs font-semibold underline decoration-[1.5px] underline-offset-[3px] hover:opacity-80 focus-visible:outline-2 focus-visible:outline-focus", brand ? "text-on-brand" : "text-brand")}>
          Ver lista<span className="sr-only"> de {label}</span>
        </button>
      )}
    </div>
  );
}

// ---- Copiar ----
export function CopyField({ value, label, secret }: { value: string; label: string; secret?: boolean }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <div className="flex items-center gap-2">
      <code className={cn("min-w-0 flex-1 truncate rounded border border-border bg-surface-muted px-2 py-1.5 font-mono text-xs", secret && "select-all")} aria-label={label}>
        {value}
      </code>
      <Button
        variant="outline"
        size="sm"
        aria-label={`Copiar ${label}`}
        onClick={async () => {
          await navigator.clipboard?.writeText(value).catch(() => undefined);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );
}

// ---- Toasts ----
interface ToastItem {
  id: number;
  tone: "success" | "danger" | "info";
  message: string;
}
const ToastCtx = React.createContext<(m: string, tone?: ToastItem["tone"]) => void>(() => undefined);
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = React.useState<ToastItem[]>([]);
  const push = React.useCallback((message: string, tone: ToastItem["tone"] = "success") => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x, { id, tone, message }]);
    setTimeout(() => setItems((x) => x.filter((i) => i.id !== id)), 5000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-[calc(100vw-2rem)] max-w-sm flex-col gap-2">
        {items.map((t) => (
          <div
            key={t.id}
            role={t.tone === "danger" ? "alert" : "status"}
            className={cn(
              "pointer-events-auto rounded-[var(--cm-radius)] border px-4 py-3 text-sm shadow-md",
              t.tone === "success" && "border-success/30 bg-success-bg text-success",
              t.tone === "danger" && "border-danger/30 bg-danger-bg text-danger",
              t.tone === "info" && "border-info/30 bg-info-bg text-info",
            )}
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => React.useContext(ToastCtx);

// ---- Confirmação (substitui window.confirm por diálogo do design system, whitelabel e acessível) ----
interface ConfirmOptions {
  title?: string;
  description: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "danger" | "primary";
}
type ConfirmFn = (opts: ConfirmOptions | string) => Promise<boolean>;
const ConfirmCtx = React.createContext<ConfirmFn>(async () => false);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<{ opts: ConfirmOptions; resolve: (v: boolean) => void } | null>(null);
  const confirm = React.useCallback<ConfirmFn>((o) => {
    const opts = typeof o === "string" ? { description: o } : o;
    return new Promise<boolean>((resolve) => setState({ opts, resolve }));
  }, []);
  const close = (v: boolean) => {
    state?.resolve(v);
    setState(null);
  };
  const o = state?.opts;
  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <Dialog
        open={!!state}
        onOpenChange={(open) => !open && close(false)}
        size="sm"
        title={o?.title ?? "Confirmar ação"}
        description={o?.description}
        footer={
          <>
            <Button variant="outline" onClick={() => close(false)}>
              {o?.cancelLabel ?? "Cancelar"}
            </Button>
            <Button variant={o?.tone === "primary" ? "primary" : "danger"} onClick={() => close(true)}>
              {o?.confirmLabel ?? "Confirmar"}
            </Button>
          </>
        }
      />
    </ConfirmCtx.Provider>
  );
}
/** Retorna `confirm(opts|texto) => Promise<boolean>`. Use: `if (!(await confirm("…"))) return;`. */
export const useConfirm = () => React.useContext(ConfirmCtx);

export function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="text-sm font-medium tabular-nums text-fg">{value}</dd>
    </div>
  );
}

export function SimulatorBadge({ label = "Simulador" }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-warning/30 bg-warning-bg px-2 py-0.5 text-xs font-medium text-warning" title="Ambiente simulado: não representa integração real validada">
      <AlertTriangle className="h-3 w-3" aria-hidden />
      {label}
    </span>
  );
}
